import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cookie, ShieldCheck, Info, List, Globe, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Cookies() {
  const { t } = useTranslation();
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: i * 0.15 },
    }),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="relative py-20 bg-black overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-3 text-white">
              {t("cookies.hero.title1")}{" "}
              <span className="text-primary">
                {t("cookies.hero.highlight")}
              </span>
            </h1>

            <p className="font-paragraph text-foreground/70 max-w-xl mx-auto text-white">
              {t("cookies.hero.description")}
            </p>
            <Cookie className="mx-auto mt-6 h-12 w-12 text-primary opacity-70" />
          </motion.div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 mb-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <div className="flex items-center gap-3 mb-3">
            <List className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl font-bold">
              {t("cookies.summary.title")}
            </h2>
          </div>

          <ul className="font-paragraph space-y-2 text-foreground/80">
            <li>
              <a
                href="#cookie-definition"
                className="hover:text-primary transition"
              >
                {t("cookies.summary.definition")}
              </a>
            </li>

            <li>
              <a href="#cookie-types" className="hover:text-primary transition">
                {t("cookies.summary.types")}
              </a>
            </li>

            <li>
              <a
                href="#cookie-purpose"
                className="hover:text-primary transition"
              >
                {t("cookies.summary.purpose")}
              </a>
            </li>

            <li>
              <a
                href="#cookie-settings"
                className="hover:text-primary transition"
              >
                {t("cookies.summary.settings")}
              </a>
            </li>

            <li>
              <a
                href="#third-parties"
                className="hover:text-primary transition"
              >
                {t("cookies.summary.thirdParties")}
              </a>
            </li>
          </ul>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 space-y-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <section id="cookie-definition">
            <div className="flex items-center gap-3 mb-3">
              <Info className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold">
  {t("cookies.definition.title")}
</h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
  {t("cookies.definition.description")}
</p>
          </section>

          <section id="cookie-types">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="h-6 w-6 text-secondary" />
              <h2 className="text-xl font-heading font-semibold">
  {t("cookies.types.title")}
</h2>
            </div>

            <ul className="list-disc pl-6 space-y-2 font-paragraph text-foreground/80">
  <li>
    <strong>{t("cookies.types.essential.label")}</strong>{" "}
    {t("cookies.types.essential.description")}
  </li>

  <li>
    <strong>{t("cookies.types.statistics.label")}</strong>{" "}
    {t("cookies.types.statistics.description")}
  </li>

  <li>
    <strong>{t("cookies.types.functional.label")}</strong>{" "}
    {t("cookies.types.functional.description")}
  </li>

  <li>
    <strong>{t("cookies.types.marketing.label")}</strong>{" "}
    {t("cookies.types.marketing.description")}
  </li>
</ul>
          </section>

          <section id="cookie-purpose">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="h-6 w-6 text-electric-blue" />
             <h2 className="text-xl font-heading font-semibold">
  {t("cookies.purpose.title")}
</h2>
            </div>

           <p className="font-paragraph text-foreground/80 leading-relaxed">
  {t("cookies.purpose.description")}
</p>
          </section>

          <section id="cookie-settings">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold">
  {t("cookies.settings.title")}
</h2>
            </div>

           <p className="font-paragraph text-foreground/80 leading-relaxed">
  {t("cookies.settings.description")}
</p>
          </section>

          <section id="third-parties">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="h-6 w-6 text-secondary" />
             <h2 className="text-xl font-heading font-semibold">
  {t("cookies.thirdParties.title")}
</h2>

            </div>

           <p className="font-paragraph text-foreground/80 leading-relaxed">
  {t("cookies.thirdParties.description")}
</p>
          </section>
        </motion.div>

        <div className="text-center mt-10">
          <Link
            to="/"
            className="inline-block font-paragraph text-primary font-semibold hover:underline"
          >
            {t("cookies.backHome")}
          </Link>
        </div>
      </main>
    </div>
  );
}
